import { Fragment, memo } from 'react';
import classNames from 'classnames';

import { common } from '@gosupersimple/types';

import { isPipelineWithParent } from '@/core/pipeline';
import { Icon } from '@/components/icon';
import { useMetadataContext } from '@/explore/metadata-context';
import { useAccountTimezone } from '@/lib/accounts/context';

import { Exploration, Model, Pipeline } from '../../types';
import {
  getOperationIcon,
  getOperationOverview,
  getSqlOperationOverview,
} from '../../pipeline/format-operation';
import { getFieldsWithRelatedFields, getPipelineStateAtIndex } from '../../pipeline/state';
import {
  getParentPipelineTitle,
  getParentPipelineColor,
  getNumberOfChildPipelines,
  flattenFieldGroups,
  flattenPipeline,
} from '../../pipeline/utils';
import { SavedPipelineHeader } from './saved-pipeline-header';
import { CombineHeader } from './combine-header';
import { Operation } from './operation';
import { isValidOperation } from '../../pipeline/operation';
import { getExplorationVariables } from '../../utils';

import styles from './pipeline-preview.module.scss';

interface PipelinePreviewProps {
  title: string;
  pipeline: Pipeline;
  models: Model[];
  exploration: Exploration;
  variables?: common.QueryVariables;
  interactive?: boolean;
  onClickParent?: () => void;
  onClickOperation?: (index: number) => void;
  rollbackIndex?: number;
}

export const PipelinePreview = memo(function PipelinePreview(props: PipelinePreviewProps) {
  const { pipeline, exploration, onClickOperation, interactive = true, rollbackIndex } = props;
  const { models, metrics } = useMetadataContext();
  const variables = props.variables ?? getExplorationVariables(exploration);
  const timezone = useAccountTimezone();

  const flattenedPipeline = flattenPipeline(pipeline, exploration);
  const parentOperationCount = flattenedPipeline.operations.length - pipeline.operations.length;
  const parentColor = getParentPipelineColor(exploration, pipeline);
  const parentTitle = getParentPipelineTitle(exploration, pipeline);
  const parentChildCount = getNumberOfChildPipelines(exploration, pipeline);

  const firstOperation = pipeline.operations.at(0);
  const hasParent = isPipelineWithParent(pipeline);

  return (
    <div className={classNames(styles.pipeline)}>
      {firstOperation?.operation !== 'joinPipeline' && firstOperation?.operation !== 'sql' && (
        <SavedPipelineHeader
          title={parentTitle ?? props.title}
          icon={hasParent ? 'Instance' : 'Model'}
          onClick={hasParent && interactive ? props.onClickParent : undefined}
          color={parentColor}
          childCount={parentChildCount}
        />
      )}

      {pipeline.operations.map((operation, i) => {
        if (operation.operation === 'joinPipeline') {
          return (
            <CombineHeader
              key={i}
              pipeline={pipeline}
              operation={operation}
              exploration={exploration}
              models={models}
            />
          );
        }

        if (operation.operation === 'sql') {
          return (
            <Operation
              key={i}
              invalid={!isValidOperation(operation)}
              disabled={
                (operation.disabled ?? false) || (rollbackIndex !== undefined && i >= rollbackIndex)
              }
              icon="Database"
              description={getSqlOperationOverview(operation)}
            />
          );
        }
        const fullPipelineIdx = parentOperationCount + i;
        const pipelineState = getPipelineStateAtIndex(pipeline, i, {
          models,
          variables,
          metrics,
          exploration,
        });

        return (
          <Fragment key={i}>
            <Icon name="ChevronRight" size={14} className={styles.separator} />
            <Operation
              invalid={!isValidOperation(operation)}
              disabled={
                (operation.disabled ?? false) || (rollbackIndex !== undefined && i >= rollbackIndex)
              }
              icon={getOperationIcon(operation)}
              description={getOperationOverview(operation, {
                fields: flattenFieldGroups(getFieldsWithRelatedFields(pipelineState, models)),
                model: pipelineState.model,
                variables,
                timezone,
              })}
              onClick={
                onClickOperation && interactive
                  ? () => onClickOperation(fullPipelineIdx)
                  : undefined
              }
            />
          </Fragment>
        );
      })}
    </div>
  );
});
